import { Suspense, lazy } from 'react';

import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { RequireAuth } from './RequireAuth';

const HomePage = lazy(() => import('@pages/HomePage').then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('@pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const OnboardingPage = lazy(() =>
  import('@pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const DashboardPage = lazy(() =>
  import('@pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const CalendarPage = lazy(() =>
  import('@pages/CalendarPage').then((m) => ({ default: m.CalendarPage })),
);
const ClientsPage = lazy(() =>
  import('@pages/ClientsPage').then((m) => ({ default: m.ClientsPage })),
);
const ClientViewPage = lazy(() =>
  import('@pages/ClientViewPage').then((m) => ({ default: m.ClientViewPage })),
);
const SpecialtiesPage = lazy(() =>
  import('@pages/SpecialtiesPage').then((m) => ({ default: m.SpecialtiesPage })),
);
const StaffPage = lazy(() => import('@pages/StaffPage').then((m) => ({ default: m.StaffPage })));
const StaffMemberViewPage = lazy(() =>
  import('@pages/StaffMemberViewPage').then((m) => ({ default: m.StaffMemberViewPage })),
);
const InvoicesPage = lazy(() =>
  import('@pages/InvoicesPage').then((m) => ({ default: m.InvoicesPage })),
);
const ReportsPage = lazy(() =>
  import('@pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const SettingsPage = lazy(() =>
  import('@pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const BusinessDetailsPage = lazy(() =>
  import('@pages/BusinessDetailsPage').then((m) => ({ default: m.BusinessDetailsPage })),
);
const BrandingPage = lazy(() =>
  import('@pages/BrandingPage').then((m) => ({ default: m.BrandingPage })),
);
const BusinessHoursPage = lazy(() =>
  import('@pages/BusinessHoursPage').then((m) => ({ default: m.BusinessHoursPage })),
);
const HolidaysPage = lazy(() =>
  import('@pages/HolidaysPage').then((m) => ({ default: m.HolidaysPage })),
);
const ScheduleSettingsPage = lazy(() =>
  import('@pages/ScheduleSettingsPage').then((m) => ({ default: m.ScheduleSettingsPage })),
);
const ChartTemplatesPage = lazy(() =>
  import('@pages/ChartTemplatesPage').then((m) => ({ default: m.ChartTemplatesPage })),
);
const ChartTemplateViewPage = lazy(() =>
  import('@pages/ChartTemplateViewPage').then((m) => ({ default: m.ChartTemplateViewPage })),
);
const NotFoundPage = lazy(() =>
  import('@pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const StatusPage = lazy(() => import('@pages/StatusPage').then((m) => ({ default: m.StatusPage })));
const InviteAcceptPage = lazy(() =>
  import('@pages/InviteAcceptPage').then((m) => ({ default: m.InviteAcceptPage })),
);

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function lazy_wrap(Component: React.ComponentType) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

const router = createBrowserRouter([
  { path: '/', element: lazy_wrap(HomePage) },
  { path: '/login', element: lazy_wrap(LoginPage) },
  { path: '/register', element: lazy_wrap(OnboardingPage) },
  { path: '/resources/status', element: lazy_wrap(StatusPage) },
  { path: '/invite/accept', element: lazy_wrap(InviteAcceptPage) },
  {
    element: <RequireAuth />,
    children: [
      { path: '/account', element: <Navigate to="/account/dashboard" replace /> },
      { path: '/account/dashboard', element: lazy_wrap(DashboardPage) },
      { path: '/account/calendar', element: lazy_wrap(CalendarPage) },
      { path: '/account/clients', element: lazy_wrap(ClientsPage) },
      { path: '/account/clients/:clientId/view', element: lazy_wrap(ClientViewPage) },
      { path: '/account/specialties', element: lazy_wrap(SpecialtiesPage) },
      { path: '/account/staff', element: lazy_wrap(StaffPage) },
      { path: '/account/staff/:staffId', element: lazy_wrap(StaffMemberViewPage) },
      { path: '/account/invoices', element: lazy_wrap(InvoicesPage) },
      { path: '/account/reports', element: lazy_wrap(ReportsPage) },
      { path: '/account/settings', element: lazy_wrap(SettingsPage) },
      { path: '/account/settings/business-details', element: lazy_wrap(BusinessDetailsPage) },
      { path: '/account/settings/branding', element: lazy_wrap(BrandingPage) },
      { path: '/account/settings/business-hours', element: lazy_wrap(BusinessHoursPage) },
      { path: '/account/settings/holidays', element: lazy_wrap(HolidaysPage) },
      { path: '/account/settings/schedule', element: lazy_wrap(ScheduleSettingsPage) },
      { path: '/account/settings/chart-templates', element: lazy_wrap(ChartTemplatesPage) },
      {
        path: '/account/settings/chart-templates/:templateId',
        element: lazy_wrap(ChartTemplateViewPage),
      },
    ],
  },
  { path: '*', element: lazy_wrap(NotFoundPage) },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
